import React, { useState } from "react";
import { useParams } from "react-router-dom";
import SectionWrapper from "../../Widgets/SectionWrapper";
import RowOptions from "../../Widgets/RowOptions";
import DataTable, { DataTableColumn, DataTableRow } from "../../Widgets/DataTable";
import FamilyMedicalHistoryModal, { FamilyMedicalHistoryFormValues } from "./FamilyMedicalHistoryModal";
import useGetFamilyMedicalHistory from "hooks/api/client/useGetFamilyMedicalHistory";
import useCreateFamilyMedicalHistory from "hooks/api/client/useCreateFamilyMedicalHistory";
import useUpdateFamilyMedicalHistory from "hooks/api/client/useUpdateFamilyMedicalHistory";
import useDeleteFamilyMedicalHistory from "hooks/api/client/useDeleteFamilyMedicalHistory";

const BASE_COLUMNS: DataTableColumn[] = [
  { key: "familyMemberName", label: "Family Member Name" },
  { key: "relationToChild", label: "Relation to Child" },
  { key: "diagnoses", label: "Diagnoses" },
  { key: "currentTreatments", label: "Current Treatments" },
  { key: "pastTreatments", label: "Past Treatments" },
];

const EDIT_COLUMN: DataTableColumn = { key: "actions", label: "" };

export const FamilyMedicalHistory: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { familyMedicalHistory, refetchFamilyMedicalHistory } = useGetFamilyMedicalHistory(clientIdNum);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const { createFamilyMedicalHistoryMutation, isCreateFamilyMedicalHistoryLoading } = useCreateFamilyMedicalHistory({
    onSuccess: () => {
      refetchFamilyMedicalHistory();
      closeModal();
    },
  });

  const { updateFamilyMedicalHistoryMutation, isUpdateFamilyMedicalHistoryLoading } = useUpdateFamilyMedicalHistory({
    onSuccess: () => {
      refetchFamilyMedicalHistory();
      closeModal();
    },
  });

  const { deleteFamilyMedicalHistoryMutation } = useDeleteFamilyMedicalHistory({
    onSuccess: () => {
      refetchFamilyMedicalHistory();
    },
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DataTableRow | null>(null);

  const columns = [...BASE_COLUMNS, EDIT_COLUMN];

  const rows: DataTableRow[] = familyMedicalHistory.map((item: any) => ({
    id: String(item.id),
    familyMemberName: item.familyMember || "-",
    relationToChild: item.relationship || "-",
    diagnoses: item.diagnoses || "-",
    currentTreatments: item.currentTreatments || "-",
    pastTreatments: item.pastTreatments || "-",
  }));

  const handleSave = (values: FamilyMedicalHistoryFormValues) => {
    if (!clientIdNum) return;
    const payload = {
      familyMember: values.familyMember || null,
      relationship: values.relationship || null,
      diagnoses: values.diagnoses || null,
      currentTreatments: values.currentTreatments || null,
      pastTreatments: values.pastTreatments || null,
    };

    if (selectedRow) {
      updateFamilyMedicalHistoryMutation({ id: Number(selectedRow.id), payload });
    } else {
      createFamilyMedicalHistoryMutation({ clientId: clientIdNum, payload });
    }
  };

  return (
    <SectionWrapper
      title="Family Medical History"
      description="Past and current family medical records."
      hideButtons
    >
      <DataTable
        columns={columns}
        rows={rows}
        ariaLabel="Family medical history"
        emptyMessage="No family medical history records"
        onAddNew={() => { setSelectedRow(null); setModalOpen(true); }}
        renderCell={(col, row) => {
          if (col.key === "actions") {
            return (
              <RowOptions
                onEdit={() => { setSelectedRow(row); setModalOpen(true); }}
                onDelete={() => deleteFamilyMedicalHistoryMutation(Number(row.id))}
              />
            );
          }
          return row[col.key] || "-";
        }}
      />

      <FamilyMedicalHistoryModal
        open={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        isSaving={isCreateFamilyMedicalHistoryLoading || isUpdateFamilyMedicalHistoryLoading}
        initialValues={selectedRow ? {
          familyMember: selectedRow.familyMemberName !== "-" ? selectedRow.familyMemberName : "",
          relationship: selectedRow.relationToChild !== "-" ? selectedRow.relationToChild : "",
          diagnoses: selectedRow.diagnoses !== "-" ? selectedRow.diagnoses : "",
          currentTreatments: selectedRow.currentTreatments !== "-" ? selectedRow.currentTreatments : "",
          pastTreatments: selectedRow.pastTreatments !== "-" ? selectedRow.pastTreatments : "",
        } : undefined}
      />
    </SectionWrapper>
  );
};

export default FamilyMedicalHistory;
